import { appMode, AppMode, cfgAuthedBasePath } from '@infinitro/config';
import { get } from 'svelte/store';
import { loggedIn } from './auth';
import { defaultLocale, locale, pathLocale } from './locale';
const LANGPATH_APPS = [AppMode.Public];
const BASEPATH_APPS = [AppMode.Player, AppMode.Mod];
// const SITE_APPS = [AppMode.Public, ...BASEPATH_APPS];
const AUTHED_BASEPATH = cfgAuthedBasePath();
const EXCLUDE_PATHS_FROM_TRANSLATION = ['/blog'];
const REDIRECT_LOOP_TIMEOUT = 5000;
/** True for absolute http(s) URLs (e.g. Telegram, external campaigns). Not for in-app paths. */
export const isAbsoluteHttpUrl = (href) => /^https?:\/\//i.test(href.trim());
// paths excluded from path translation
export const isExcludedPath = (href) => {
    return EXCLUDE_PATHS_FROM_TRANSLATION.some((path) => href.startsWith(path));
};
// Public + logged-out: prefix internal hrefs with /xx/ only when `pathLocale` is set (user is on /es/…).
// Logged-in public: never add /xx/ on generated links (CTO: locale in URL not required when authenticated).
// Unprefixed URL (no /xx/ in address bar): never add a prefix from the dropdown alone — `locale` can be
// non-default from preference, but links stay unprefixed until the user is on a prefixed route (matches
// `selectedLocale` not calling gotoPathLocale when !pathLang).
export const toLocalePath = (href) => {
    const l = get(locale);
    const pl = get(pathLocale);
    if (!l || !LANGPATH_APPS.includes(appMode))
        return href;
    if (get(loggedIn))
        return href;
    if (isExcludedPath(href))
        return href;
    if (pl) {
        if (href.startsWith(`/${pl}`))
            return href;
        return `/${pl}${href}`;
    }
    return href;
};
// adds the app path to the path if it is a site app and user is authenticated
export const toAppPath = (href, isLoggedIn) => {
    if (!BASEPATH_APPS.includes(appMode))
        return href;
    if (!isLoggedIn || isExcludedPath(href))
        return href;
    if (!href.startsWith('/' + AUTHED_BASEPATH))
        href = '/' + AUTHED_BASEPATH + href;
    return href;
};
// Root-relative URL for navigation; respects auth base path and `toLocalePath` (unprefixed + dropdown
// does not inject /xx/ — see `toLocalePath`).
export const toSiteUrl = (href, isLoggedIn) => {
    if (isAbsoluteHttpUrl(href))
        return href;
    if (!href.startsWith('/'))
        href = '/' + href;
    // site URLs must end with a slash (trailingSlash = 'always' in SvelteKit config)
    if (!href.endsWith('/') && !href.includes('?'))
        href = href + '/';
    // blog URLs must not end with a slash
    if (href.includes('/blog/'))
        href = href.slice(0, -1);
    return toAppPath(toLocalePath(href), get(loggedIn) || isLoggedIn || false);
};
// Same as `toSiteUrl`, plus `isCurrent` vs the active resolved href.
export const toSiteUrlWithActive = (href, current, isLoggedIn) => {
    href = toSiteUrl(href, isLoggedIn);
    const isCurrent = href === current;
    return [href, isCurrent];
};
export const pathPlayerToPublic = () => {
    // strips the player part from the path
    const parts = document.location.pathname
        .split('/')
        .filter((part) => part.length > 0)
        .filter((part) => part !== AUTHED_BASEPATH);
    return (parts.length > 0 ? '/' + parts.join('/') + '/' : '/') + document.location.search;
};
// this is used for the redirect from public to authed upon authentication
export const pathPublicToPlayer = (authedBasePath) => {
    if (!authedBasePath)
        authedBasePath = AUTHED_BASEPATH;
    const currentLocale = get(pathLocale);
    // strip locale prefix (/xx/) — player has no locale-prefixed URLs
    const parts = document.location.pathname
        .split('/')
        .filter((part) => part.length > 0)
        .filter((part) => part !== currentLocale);
    if (!parts.includes(authedBasePath))
        parts.unshift(authedBasePath);
    const newPath = '/' + parts.join('/') + '/' + document.location.search;
    return newPath;
};
export const handlePublicToPlayerRedirect = (redirectTo) => {
    if (document.location.search?.includes('logout') ||
        document.location.search?.includes('noredirect')) {
        return;
    }
    let redirectToUrl = redirectTo;
    if (!redirectToUrl) {
        redirectToUrl = sessionStorage.getItem('redirectTo');
        if (redirectToUrl && redirectToUrl.length > 0) {
            console.log(`found redirectTo: ${redirectToUrl}`);
            sessionStorage.removeItem('redirectTo');
        }
        else {
            redirectToUrl = null;
        }
    }
    if (redirectToUrl &&
        redirectToUrl?.startsWith('http') &&
        !document.location.href.startsWith(redirectToUrl)) {
        console.log(`redirecting from ${document.location.href} to ${redirectToUrl}`);
        window.location.href = redirectToUrl;
        return;
    }
    const authedBasePath = redirectToUrl ?? cfgAuthedBasePath();
    const authedPath = pathPublicToPlayer(authedBasePath);
    const currentPath = `${document.location.href}${document.location.search}`;
    if (currentPath === authedBasePath ||
        currentPath === authedPath ||
        currentPath.startsWith(authedBasePath) ||
        currentPath.startsWith(authedPath)) {
        return;
    }
    redirectNoloop(authedPath);
};
export const handlePlayerToPublicRedirect = () => {
    const publicPath = pathPlayerToPublic();
    const currentPath = `${document.location.href}${document.location.search}`;
    if (currentPath === publicPath) {
        return;
    }
    redirectNoloop(publicPath);
};
export const getLastRedirect = () => {
    try {
        const key = `lastRedirectFrom-${document.location.href}`;
        const lastRedirect = sessionStorage.getItem(key);
        if (!lastRedirect || !lastRedirect.includes('|'))
            return [null, null];
        const path = lastRedirect.split('|')[0];
        const time = parseInt(lastRedirect.split('|')[1]);
        return [path, time];
    }
    catch (e) {
        console.log(`getLastRedirect error [${e}]`);
    }
    return [null, null];
};
export const redirectNoloop = (path) => {
    try {
        const [lastPath, lastTime] = getLastRedirect();
        if (lastPath === path && lastTime && lastTime > Date.now() - REDIRECT_LOOP_TIMEOUT) {
            console.log(`redirectNoloop loop detected [${path}] at ${Date.now().toLocaleString}`);
            return false;
        }
        const key = `lastRedirectFrom-${document.location.href}`;
        sessionStorage.setItem(key, `${path}|${Date.now()}`);
        console.log(`redirecting to [${path}] at ${Date.now().toLocaleString()}`);
        window.location.replace(path);
        return true;
    }
    catch (e) {
        console.log(`redirectNoloop error [${e}]`);
        return false;
    }
};
